/**
 * RevisionHistoryDialog — 게시글 수정 이력 다이얼로그
 *
 * 수정 이력을 시간순으로 표시하며, 각 revision을 펼쳐
 * 해당 시점의 제목과 본문을 TipTap 읽기 전용으로 확인 가능.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Trans, t } from '@lingui/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { fetchPostRevisions } from '../../services/BoardService';
import { History, ChevronDown, ChevronUp } from 'lucide-react';
import BoardRichEditor from './BoardRichEditor';
import { cn } from '../../lib/utils';

interface Props {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Revision {
  id: string;
  revision_number: number;
  title: string;
  content: any;
  plain_text: string;
  edited_by: string;
  edited_at: string;
}

const RevisionHistoryDialog: React.FC<Props> = ({ postId, open, onOpenChange }) => {
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!open || !postId) return;
    setLoading(true);
    try {
      const data = await fetchPostRevisions(postId);
      setRevisions(data as Revision[]);
    } catch (err) {
      console.error('[RevisionHistory] 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [open, postId]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="size-5" />
            <Trans>Revision history</Trans>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Trans>Loading...</Trans>
          </div>
        ) : revisions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
            <History className="size-8 opacity-30" />
            <span><Trans>No revision history</Trans></span>
          </div>
        ) : (
          <div className="space-y-2">
            {revisions.map((rev) => {
              const isExpanded = expandedId === rev.id;
              return (
                <div
                  key={rev.id}
                  className="border border-border/60 rounded-lg overflow-hidden"
                >
                  {/* 헤더 — 클릭으로 펼치기/접기 */}
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
                    onClick={() => setExpandedId(isExpanded ? null : rev.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs font-mono text-muted-foreground shrink-0">
                        v{rev.revision_number}
                      </span>
                      <span className="text-sm font-medium truncate">
                        {rev.title || t`(no title)`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(rev.edited_at)}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="size-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="size-4 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  {/* 본문 — 펼쳐진 상태 */}
                  {isExpanded && (
                    <div className="p-4 border-t border-border/40 bg-background/60">
                      <div className="text-xs text-muted-foreground mb-2">
                        <Trans>Body at this version:</Trans>
                      </div>
                      <BoardRichEditor
                        initialContent={rev.content}
                        editable={false}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RevisionHistoryDialog;
